import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { track } from './analytics.js';
import { ChallengesProvider, useChallenges } from './api/challenges.js';
import { useSession } from './api/session.js';
import { ActiveGameBanner } from './components/ActiveGameBanner.js';
import { HelpDialog } from './components/HelpDialog.js';
import { NotificationPopup } from './components/NotificationPopup.js';
import { WhatsNewDialog } from './components/WhatsNewDialog.js';
import { AccountPage } from './routes/AccountPage.js';
import { GamePage } from './routes/GamePage.js';
import { LobbyPage } from './routes/LobbyPage.js';
import { LoginPage } from './routes/LoginPage.js';
import { PrivacyPage } from './routes/PrivacyPage.js';
import { RegisterPage } from './routes/RegisterPage.js';
import { ReplayPage } from './routes/ReplayPage.js';
import { useOnlineStatus } from './useOnlineStatus.js';

/** Motyw (szkic papieru): niezależny od jasności — patrz `mode` niżej. */
export type Skin = 'notebook' | 'blackboard';
/** Jasność widoku, osobno od motywu: pozwala np. na ciemny zeszyt albo jasną tablicę. */
export type Mode = 'light' | 'dark';

export interface ThemeOption {
  readonly value: string;
  readonly skin: Skin;
  readonly mode: Mode;
  readonly label: string;
}

const prefersDarkMode = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;

/**
 * Cztery kombinacje `Skin`×`Mode` jako jedna lista wyboru, zamiast dwóch osobnych
 * przycisków-przełączników — prostsze dla użytkownika, mimo że pod spodem to wciąż
 * dwie niezależne osie (`data-theme`/`data-mode` w `tokens.css`). Stan wyboru mieszka
 * tutaj (w `App`), nie w `AccountPage` — musi działać na każdej trasie, nie tylko na
 * `/konto`, ten sam wzorzec co reszta stanu globalnego dokumentu.
 */
const THEME_OPTIONS: readonly ThemeOption[] = [
  { value: 'notebook-light', skin: 'notebook', mode: 'light', label: 'Zeszyt — jasny' },
  { value: 'notebook-dark', skin: 'notebook', mode: 'dark', label: 'Zeszyt — ciemny' },
  { value: 'blackboard-light', skin: 'blackboard', mode: 'light', label: 'Tablica — jasna' },
  { value: 'blackboard-dark', skin: 'blackboard', mode: 'dark', label: 'Tablica — ciemna' },
];

/**
 * „wersja 4c279ea · 24.09.2026, 20:41” — SHA i data commita, z którego zbudowano ten klient
 * (`vite.config.ts`). Pozwala sprawdzić na konkretnym urządzeniu, czy dotarło ostatnie
 * wdrożenie (np. telefon trzymany na starej wersji przez service workera), a SHA jest tym
 * samym `release`, który widać przy zgłoszeniach w Sentry.
 */
function versionLabel(): string | null {
  const sha = import.meta.env.VITE_RELEASE;
  if (!sha) return null;
  const iso = import.meta.env.VITE_RELEASE_DATE;
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return `wersja ${sha}`;
  const when = date.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `wersja ${sha} · ${when}`;
}

const VERSION_LABEL = versionLabel();

export function App() {
  const [skin, setSkin] = useState<Skin>('notebook');
  const [mode, setMode] = useState<Mode>(() => (prefersDarkMode() ? 'dark' : 'light'));
  const online = useOnlineStatus();
  const { user } = useSession();
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = skin;
  }, [skin]);

  useEffect(() => {
    document.documentElement.dataset.mode = mode;
  }, [mode]);

  // Raz na wczytanie aplikacji — mobile czy desktop, jaki viewport (docs/06 §6.3).
  useEffect(() => {
    track('session_start', {
      props: {
        device: window.innerWidth < 768 ? 'mobile' : 'desktop',
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      },
    });
  }, []);

  return (
    <ChallengesProvider>
      <div className="sw-paper app">
        <header className="app__header">
          <h1 className="hand">
            <Link to="/">Statki</Link>
          </h1>
          <div className="app__header-actions">
            {/* Jeden przycisk zamiast dwóch: na `/konto` prowadzi z powrotem do lobby,
                wszędzie indziej — do konta. Nigdy oba naraz. */}
            {user && location.pathname === '/konto' && (
              <Link to="/" className="btn btn--ghost">
                Wróć do lobby
              </Link>
            )}
            {user && location.pathname !== '/konto' && (
              <Link to="/konto" className="btn btn--ghost">
                Moje konto
              </Link>
            )}
            {user && <SnoozedBadge />}
            {/* Pomoc dostępna z każdego ekranu, także przed zalogowaniem — okno nad bieżącym
                widokiem, więc otwarcie w trakcie partii niczego nie przerywa. */}
            <button
              type="button"
              className="btn btn--ghost help-btn"
              aria-label="Pomoc"
              title="Pomoc"
              onClick={() => setHelpOpen(true)}
            >
              ?
            </button>
          </div>
        </header>

        {!online && (
          <p className="offline-banner" role="status">
            Brak połączenia — pokazujemy to, co udało się zapamiętać. Rozgrywka wymaga sieci.
          </p>
        )}

        {/* Powrót do trwającej partii z każdego ekranu poza samą partią (`docs/08-decyzje.md` #24). */}
        {user && <ActiveGameBanner />}

        <main className="app__main">
          {/* Wyzwania i oferty rewanżu ponad interfejsem, z każdego ekranu — patrz `ChallengesProvider`. */}
          {user && <NotificationPopup />}
          {/* Informacja o becie i lista zmian przy starcie — poza `.app__page`, więc zmiana
              ekranu (np. automatyczne przejście do trwającej partii) jej nie zamyka. */}
          <WhatsNewDialog />
          <HelpDialog
            open={helpOpen}
            pathname={location.pathname}
            onClose={() => setHelpOpen(false)}
          />
          {/* Klucz po ścieżce = świeży montaż przy każdej zmianie ekranu — stąd animacja
              "przewracania kartki" (`docs/05` §5.4) i przy okazji czysty stan komponentu
              (np. GamePage po rewanżu zaczyna bez śladu poprzedniej partii). */}
          <div className="app__page" key={location.pathname}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/rejestracja" element={<RegisterPage />} />
              {/* Publiczny-po-znajomości-URL, jak sam endpoint: link do replayu nadaje się do udostępnienia. */}
              <Route path="/replay/:gameId" element={<ReplayPage />} />
              <Route path="/regulamin" element={<PrivacyPage />} />
              <Route element={<RequireAuth />}>
                <Route path="/" element={<LobbyPage />} />
                <Route
                  path="/konto"
                  element={
                    <AccountPage
                      skin={skin}
                      setSkin={setSkin}
                      mode={mode}
                      setMode={setMode}
                      themeOptions={THEME_OPTIONS}
                    />
                  }
                />
                <Route path="/game/:gameId" element={<GamePage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>

        <footer className="app__footer">
          <Link to="/regulamin">Polityka prywatności i regulamin</Link>
          {VERSION_LABEL && <p className="app__version">{VERSION_LABEL}</p>}
        </footer>
      </div>
    </ChallengesProvider>
  );
}

/**
 * Znacznik odłożonych „na później” wyzwań/ofert rewanżu — bez tego `NotificationPopup`
 * nie miałby żadnego śladu po kliknięciu „Później” (zaproszenie zostaje `pending` na
 * serwerze, ale popup się nie pokazuje samoistnie, patrz `ChallengesProvider`). Osobny
 * komponent, bo `useChallenges()` wymaga bycia potomkiem `ChallengesProvider`, a `App`
 * sam go dopiero renderuje.
 */
function SnoozedBadge() {
  const { snoozedCount, unsnoozeAll } = useChallenges();
  if (snoozedCount === 0) return null;
  return (
    <button type="button" className="btn btn--ghost badge-btn" onClick={unsnoozeAll}>
      🔔 {snoozedCount}
    </button>
  );
}

/** Bramka na trasy wymagające sesji. Po zalogowaniu wracamy tam, dokąd użytkownik zmierzał. */
function RequireAuth() {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <p className="app__status">Sprawdzanie sesji…</p>;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}
